import { Editor } from "@monaco-editor/react";
import clsx from "clsx";
import NightOwl from "monaco-themes/themes/Night Owl.json";
import { useRef } from "react";
import { useFontSize } from "../hooks/useFontSize";

const SQL_KEYWORDS = [
  "SELECT", "FROM", "WHERE", "INSERT", "UPDATE", "DELETE", "CREATE", "DROP", "ALTER",
  "TABLE", "INTO", "VALUES", "SET", "JOIN", "INNER", "LEFT", "RIGHT", "OUTER", "FULL",
  "ON", "AND", "OR", "NOT", "IN", "BETWEEN", "LIKE", "IS", "NULL", "AS", "ORDER", "BY",
  "GROUP", "HAVING", "LIMIT", "OFFSET", "DISTINCT", "COUNT", "SUM", "AVG", "MIN", "MAX",
  "CASE", "WHEN", "THEN", "ELSE", "END", "EXISTS", "UNION", "ALL", "ASC", "DESC",
  "PRIMARY", "KEY", "FOREIGN", "REFERENCES", "INDEX", "UNIQUE", "CHECK", "DEFAULT",
  "CONSTRAINT", "CASCADE", "TRUNCATE", "VIEW", "GRANT", "REVOKE", "COMMIT", "ROLLBACK",
  "TRANSACTION", "BEGIN", "COALESCE", "CAST", "SUBSTRING", "TRIM", "UPPER", "LOWER",
  "REPLACE", "LENGTH", "ROUND", "NOW", "CURRENT_DATE", "CURRENT_TIMESTAMP",
  "INTEGER", "VARCHAR", "TEXT", "BOOLEAN", "DATE", "TIMESTAMP", "NUMERIC", "SERIAL",
  "BIGINT", "SMALLINT", "FLOAT", "DOUBLE", "DECIMAL", "CHAR",
];

let sqlProviderRegistered = false;

const baseMonacoConfig = {
  wordWrap: true,
  tabSize: 2,
  lineNumbers: true,
  readOnly: false,
  unicodeHighlight: {
    ambiguousCharacters: false,
  },
  minimap: {
    enabled: false,
  },
  scrollBeyondLastLine: false,
  scrollbar: {
    alwaysConsumeMouseWheel: false,
  },
  quickSuggestions: true,
  suggestOnTriggerCharacters: true,
  acceptSuggestionOnEnter: "on" as const,
};

const MyNightOwl = {
  ...NightOwl,
  colors: {
    ...NightOwl.colors,
    "editor.background": "#0d1117",
  },
};

interface IMonacoProps {
  value: string;
  handleChange: (e: string) => void;
  height?: string;
  language?: string;
  cx?: string;
  options?: object;
}

export default function MonacoEditor(props: IMonacoProps) {
  const editorRef = useRef(null);
  const { fontSize } = useFontSize();
  const monacoConfig = { ...baseMonacoConfig, fontSize, lineHeight: Math.round(fontSize * 1.6) };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function handleEditorDidMount(editor: any, monaco: any) {
    editorRef.current = editor;
    monaco.editor.defineTheme("MyNightOwl", MyNightOwl);
    monaco.editor.setTheme("MyNightOwl");

    // SQL autocomplete provider (register once)
    if (!sqlProviderRegistered) {
      sqlProviderRegistered = true;
      monaco.languages.registerCompletionItemProvider("sql", {
        provideCompletionItems: (_model: any, position: any) => {
          const word = _model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };
          return {
            suggestions: SQL_KEYWORDS.map((kw) => ({
              label: kw,
              kind: monaco.languages.CompletionItemKind.Keyword,
              insertText: kw,
              range,
            })),
          };
        },
      });
    }
  }

  return (
    <Editor
      className={clsx(props.cx, "overflow-hidden !h-full eeee")}
      theme="vs-dark"
      height={props.height || "100%"}
      language={props.language || "javascript"}
      value={props.value}
      onMount={handleEditorDidMount}
      onChange={(s) => props.handleChange(s as string)}
      options={{ ...monacoConfig, ...props.options }}
    />
  );
}
